import React, { createContext, useContext, useCallback, useEffect, ReactNode } from 'react';
import { useDomainFetch } from './hooks/useDomainFetch';
import { useAuth } from './AuthContext';
import { useSettings } from './SettingsContext';
import {
  TankLevelTelemetryResponse,
  TankLevelTimeSeriesResponse,
  LiquidTankAnalyticsSummary,
  LiquidTankFillEvent,
} from '../types/telemetry';
import { API_BASE_URL } from '../config/api';

interface TankDataContextType {
  // Sub-domain data
  tankLevels: TankLevelTelemetryResponse[] | null;
  tankLevelTelemetry: TankLevelTimeSeriesResponse[] | null;
  tankAnalytics: LiquidTankAnalyticsSummary[] | null;
  tankFillEvents: LiquidTankFillEvent[] | null;

  // Aggregate state
  loading: boolean;
  error: string | null;
  isStale: boolean;
  lastFetch: number | null;

  // Actions
  refetch: (force?: boolean) => Promise<void>;
  refetchTankLevelTelemetry: (force?: boolean, queryParams?: Record<string, string>) => Promise<void>;
  refetchTankAnalytics: (force?: boolean, queryParams?: Record<string, string>) => Promise<void>;
  refetchTankFillEvents: (force?: boolean, queryParams?: Record<string, string>) => Promise<void>;
}

export const TankDataContext = createContext<TankDataContextType | undefined>(undefined);
export type { TankDataContextType };

interface TankDataProviderProps {
  children: ReactNode;
}

export function TankDataProvider({ children }: TankDataProviderProps) {
  const { refreshToken, logout } = useAuth();
  const { settings } = useSettings();

  const getToken = useCallback(
    () => (typeof window !== 'undefined' ? localStorage.getItem('tb_token') : null),
    [],
  );

  const onUnauthorized = useCallback(
    async (makeRequest: () => Promise<Response>): Promise<Response> => {
      const refreshTokenValue =
        typeof window !== 'undefined' ? localStorage.getItem('tb_refresh_token') : null;
      if (refreshTokenValue) {
        const refreshSuccess = await refreshToken();
        if (refreshSuccess) {
          return makeRequest();
        } else {
          logout();
        }
      } else {
        logout();
      }
      return new Response(null, { status: 401 });
    },
    [refreshToken, logout],
  );

  // Each sub-domain gets its own useDomainFetch instance (Pitfall 5 avoidance)

  // --- tankLevels ---
  const {
    state: tankLevelsState,
    refetch: refetchTankLevels,
  } = useDomainFetch<TankLevelTelemetryResponse[]>({
    endpoint: `${API_BASE_URL}/api/tank-levels`,
    refreshInterval: settings.refreshIntervals.devices,
    getToken,
    onUnauthorized,
  });

  // --- tankLevelTelemetry (time series from pressure sensors) ---
  const {
    state: tankLevelTelemetryState,
    refetch: refetchTankLevelTelemetry,
  } = useDomainFetch<TankLevelTimeSeriesResponse[]>({
    endpoint: `${API_BASE_URL}/api/tank-level-telemetry`,
    refreshInterval: settings.refreshIntervals.devices,
    getToken,
    onUnauthorized,
  });

  // --- tankAnalytics (fleet summary) ---
  const {
    state: tankAnalyticsState,
    refetch: refetchTankAnalytics,
  } = useDomainFetch<LiquidTankAnalyticsSummary[]>({
    endpoint: `${API_BASE_URL}/api/tank-analytics/fleet-summary`,
    refreshInterval: settings.refreshIntervals.devices,
    getToken,
    onUnauthorized,
  });

  // --- tankFillEvents ---
  const {
    state: tankFillEventsState,
    refetch: refetchTankFillEvents,
  } = useDomainFetch<LiquidTankFillEvent[]>({
    endpoint: `${API_BASE_URL}/api/tank-analytics/fill-events`,
    refreshInterval: settings.refreshIntervals.devices,
    getToken,
    onUnauthorized,
  });

  // Initial fetch on mount for stale sub-domains
  useEffect(() => {
    if (tankLevelsState.isStale || !tankLevelsState.lastFetch) {
      refetchTankLevels();
    }
    if (tankLevelTelemetryState.isStale || !tankLevelTelemetryState.lastFetch) {
      refetchTankLevelTelemetry();
    }
    if (tankAnalyticsState.isStale || !tankAnalyticsState.lastFetch) {
      refetchTankAnalytics();
    }
    if (tankFillEventsState.isStale || !tankFillEventsState.lastFetch) {
      refetchTankFillEvents(false, { hours: '17520' }); // 2 years — fill events are rare
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run only once on mount

  // Auto-refresh when marked stale by interval
  useEffect(() => {
    if (tankLevelsState.isStale && tankLevelsState.lastFetch) {
      refetchTankLevels();
    }
  }, [tankLevelsState.isStale, tankLevelsState.lastFetch, refetchTankLevels]);

  useEffect(() => {
    if (tankLevelTelemetryState.isStale && tankLevelTelemetryState.lastFetch) {
      refetchTankLevelTelemetry();
    }
  }, [tankLevelTelemetryState.isStale, tankLevelTelemetryState.lastFetch, refetchTankLevelTelemetry]);

  useEffect(() => {
    if (tankAnalyticsState.isStale && tankAnalyticsState.lastFetch) {
      refetchTankAnalytics();
    }
  }, [tankAnalyticsState.isStale, tankAnalyticsState.lastFetch, refetchTankAnalytics]);

  useEffect(() => {
    if (tankFillEventsState.isStale && tankFillEventsState.lastFetch) {
      refetchTankFillEvents();
    }
  }, [tankFillEventsState.isStale, tankFillEventsState.lastFetch, refetchTankFillEvents]);

  const refetch = useCallback(async (force = false): Promise<void> => {
    await Promise.all([
      refetchTankLevels(force),
      refetchTankLevelTelemetry(force),
      refetchTankAnalytics(force),
      refetchTankFillEvents(force),
    ]);
  }, [refetchTankLevels, refetchTankLevelTelemetry, refetchTankAnalytics, refetchTankFillEvents]);

  const loading =
    tankLevelsState.loading ||
    tankLevelTelemetryState.loading ||
    tankAnalyticsState.loading ||
    tankFillEventsState.loading;

  const error =
    tankLevelsState.error ||
    tankLevelTelemetryState.error ||
    tankAnalyticsState.error ||
    tankFillEventsState.error;

  const isStale =
    tankLevelsState.isStale ||
    tankLevelTelemetryState.isStale ||
    tankAnalyticsState.isStale ||
    tankFillEventsState.isStale;

  const lastFetchValues = [
    tankLevelsState.lastFetch,
    tankLevelTelemetryState.lastFetch,
    tankAnalyticsState.lastFetch,
    tankFillEventsState.lastFetch,
  ].filter((f): f is number => f !== null);
  const lastFetch = lastFetchValues.length > 0 ? Math.min(...lastFetchValues) : null;

  const contextValue: TankDataContextType = {
    tankLevels: tankLevelsState.data,
    tankLevelTelemetry: tankLevelTelemetryState.data,
    tankAnalytics: tankAnalyticsState.data,
    tankFillEvents: tankFillEventsState.data,
    loading,
    error,
    isStale,
    lastFetch,
    refetch,
    refetchTankLevelTelemetry,
    refetchTankAnalytics,
    refetchTankFillEvents,
  };

  return (
    <TankDataContext.Provider value={contextValue}>
      {children}
    </TankDataContext.Provider>
  );
}

export function useTankData(): TankDataContextType {
  const context = useContext(TankDataContext);
  if (context === undefined) {
    throw new Error('useTankData must be used within a TankDataProvider');
  }
  return context;
}

export default TankDataProvider;
